import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
    ChevronDown,
    Circle,
    CloudUpload,
    Eye,
    Files,
    FormInput,
    Image as ImageIcon,
    Loader2,
    Minus,
    MoveUpRight,
    PencilLine,
    Signature,
    EyeOff,
    LayoutTemplate,
    Square,
    Table,
    TextCursorInput,
    Type,
    type LucideIcon, StickyNote, MoveVertical, Redo2, Undo2 } from "@fg/icons";
import { cn } from "@/lib/utils";
import type { EditTool, PdfMode, ShapeKind } from "./editTypes";

// ---------- View / Edit switch ----------

export function ModeSwitch({
    mode, onChange, editDisabled, viewDisabled, editTitle
}: {
    mode: PdfMode;
    onChange: (mode: PdfMode) => void;
    /** No document, still opening, or a view-only shared file. */
    editDisabled: boolean;
    /** A save in flight. */
    viewDisabled: boolean;
    editTitle: string;
}) {
    return (
        <div role="group" aria-label="Mode" className="inline-flex shrink-0 overflow-hidden rounded border border-foreground/20">
            <SegButton icon={Eye} on={mode === "view"} disabled={viewDisabled && mode !== "view"} onClick={() => onChange("view")} title="View, annotate, comment and sign">
                View
            </SegButton>
            <SegButton icon={PencilLine} on={mode === "edit"} disabled={editDisabled && mode !== "edit"} onClick={() => onChange("edit")} title={editTitle} divider>
                Edit
            </SegButton>
        </div>
    );
}

function SegButton({
    icon: Icon, on, disabled, onClick, title, divider, children
}: {
    icon: LucideIcon;
    on: boolean;
    disabled?: boolean;
    onClick: () => void;
    title?: string;
    divider?: boolean;
    children: ReactNode;
}) {
    return (
        <button
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={onClick}
            title={title}
            className={cn(
                "flex h-7 items-center gap-1.5 px-2.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
                divider && "border-l border-foreground/20",
                on ? "bg-primary/10 font-semibold text-primary" : "text-muted-foreground hover:bg-accent hover:text-foreground"
            )}
        >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            {children}
        </button>
    );
}

// ---------- Edit toolbar ----------

export interface EditToolbarProps {
    tool: EditTool;
    formOpen: boolean;
    edits: number;
    saving: boolean;
    /** False: Save to Drive asks where to save a copy. */
    inPlace: boolean;
    hint: string | null;
    onEditText: () => void;
    onAddText: () => void;
    onNote: () => void;
    canUndo: boolean;
    canRedo: boolean;
    onUndo: () => void;
    onRedo: () => void;
    onSpace: () => void;
    onTable: () => void;
    onPageMarks: () => void;
    redactCount: number;
    onRedact: () => void;
    onApplyRedactions: () => void;
    onImage: () => void;
    onShape: (kind: ShapeKind) => void;
    onSign: () => void;
    onFormFields: () => void;
    onPages: () => void;
    onDiscard: () => void;
    onSave: () => void;
}

/** The PDF-red "Editing" bar under the file bar — only in Edit mode. */
export function EditToolbar(p: EditToolbarProps) {
    return (
        <div className="pdf-edit-bar shrink-0 border-b">
            <div
                role="toolbar"
                aria-label="Edit tools"
                className="flex items-center gap-0.5 overflow-x-auto px-2.5 py-1.5 text-sm [scrollbar-width:none]"
            >
                <b className="pdf-edit-text mr-1.5 shrink-0 font-semibold">Editing</b>
                <ToolButton icon={Undo2} onClick={p.onUndo} disabled={!p.canUndo} title="Undo (Ctrl+Z or Alt+Backspace)">
                    Undo
                </ToolButton>
                <ToolButton icon={Redo2} onClick={p.onRedo} disabled={!p.canRedo} title="Redo (Ctrl+Y, Ctrl+Shift+Z or Alt+Shift+Backspace)">
                    Redo
                </ToolButton>
                <span className="mx-1 h-5 w-px shrink-0 bg-border" aria-hidden />
                <ToolButton
                    icon={Type}
                    pressed={p.tool === "edit-text"}
                    onClick={p.onEditText}
                    title="Edit text — click a paragraph to change it (or drag over words); they're covered and your new text goes on top"
                >
                    Edit text
                </ToolButton>
                <ToolButton icon={TextCursorInput} pressed={p.tool === "add-text"} onClick={p.onAddText} title="Add a text box — click where it goes">
                    Add text
                </ToolButton>
                <ToolButton icon={StickyNote} pressed={p.tool === "note"} onClick={p.onNote} title="Add a note — click where the note icon goes, then type">
                    Note
                </ToolButton>
                <ToolButton icon={ImageIcon} onClick={p.onImage} title="Place a PNG or JPEG image on this page">
                    Image
                </ToolButton>
                <ShapeMenu pressed={p.tool === "shape"} onPick={p.onShape} />
                <ToolButton icon={Table} pressed={p.tool === "table"} onClick={p.onTable} title="Insert a table — choose rows and columns, then click where it goes">
                    Table
                </ToolButton>
                <ToolButton icon={LayoutTemplate} onClick={p.onPageMarks} title="Add a header, footer, page or Bates numbers, or a watermark to some or all pages">
                    Header &amp; footer
                </ToolButton>
                <ToolButton icon={EyeOff} pressed={p.tool === "redact"} onClick={p.onRedact} title="Mark text, pictures or any area to remove permanently — drag over it, then Apply redactions">
                    Redact
                </ToolButton>
                {p.redactCount > 0 ? (
                    <ToolButton icon={EyeOff} onClick={p.onApplyRedactions} title="Permanently remove everything under the marked areas">
                        Apply redactions ({p.redactCount})
                    </ToolButton>
                ) : null}
                <ToolButton icon={Signature} pressed={p.tool === "sign"} onClick={p.onSign} title="Draw, type or upload a signature, then place it">
                    Sign
                </ToolButton>
                <ToolButton icon={FormInput} pressed={p.formOpen} onClick={p.onFormFields} title="Add or change form fields">
                    Form fields
                </ToolButton>
                <ToolButton icon={MoveVertical} pressed={p.tool === "space"} onClick={p.onSpace} title="Add space — click a line; everything below it moves down">
                    Space
                </ToolButton>
                <ToolButton icon={Files} onClick={p.onPages} title="Reorder, rotate, delete or insert pages">
                    Pages
                </ToolButton>
                <span className="min-w-2 flex-1" />
                <button
                    type="button"
                    onClick={p.onDiscard}
                    disabled={p.saving}
                    title="Throw away these edits and go back to View"
                    className="flex h-7 shrink-0 items-center rounded-md border bg-background px-2.5 text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
                >
                    Discard
                </button>
                <button
                    type="button"
                    onClick={p.onSave}
                    disabled={p.saving || p.edits === 0}
                    aria-busy={p.saving || undefined}
                    title={
                        p.edits === 0
                            ? "Nothing to save yet"
                            : p.inPlace
                                ? "Burn the edits into the PDF and save it as a new version in Drive (Ctrl+S)"
                                : "Burn the edits into the PDF and save it to a Drive folder you choose (Ctrl+S)"
                    }
                    className="ml-1.5 flex h-7 shrink-0 items-center gap-1.5 rounded-md bg-primary px-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50"
                >
                    {p.saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <CloudUpload className="h-4 w-4" aria-hidden />}
                    {p.inPlace ? "Save to Drive" : "Save to Drive…"}
                </button>
            </div>
            <p aria-live="polite" className={p.hint ? "px-3 pb-1.5 text-xs text-muted-foreground" : "sr-only"}>
                {p.hint ?? ""}
            </p>
        </div>
    );
}

function ToolButton({
    icon: Icon, pressed, onClick, title, children, disabled
}: {
    icon: LucideIcon;
    disabled?: boolean;
    /** Omit for one-shot tools (no toggle state). */
    pressed?: boolean;
    onClick: () => void;
    title: string;
    children: ReactNode;
}) {
    return (
        <button
            type="button"
            aria-pressed={pressed}
            disabled={disabled}
            onClick={onClick}
            title={title}
            className="pdf-edit-tool flex h-8 shrink-0 items-center gap-1.5 rounded px-2 disabled:pointer-events-none disabled:opacity-40 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
            <Icon className="h-4 w-4" aria-hidden />
            {children}
        </button>
    );
}

const SHAPES: { kind: ShapeKind; label: string; icon: LucideIcon }[] = [
    { kind: "Rectangle", label: "Rectangle", icon: Square },
    { kind: "Circle", label: "Ellipse", icon: Circle },
    { kind: "Line", label: "Line", icon: Minus },
    { kind: "Arrow", label: "Arrow", icon: MoveUpRight }
];

/**
 * Shape ▾ — a small menu. It opens `position: fixed` under the button so the
 * toolbar's horizontal scroll can't clip it.
 */
function ShapeMenu({ pressed, onPick }: { pressed: boolean; onPick: (kind: ShapeKind) => void }) {
    const [open, setOpen] = useState<{ left: number; top: number } | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const close = useCallback((focusButton: boolean) => {
        setOpen(null);
        if (focusButton) buttonRef.current?.focus();
    }, []);

    useEffect(() => {
        if (!open) return;
        menuRef.current?.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
        const onDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (!menuRef.current?.contains(t) && !buttonRef.current?.contains(t)) close(false);
        };
        const onScroll = () => close(false);
        window.addEventListener("mousedown", onDown);
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("mousedown", onDown);
            window.removeEventListener("resize", onScroll);
        };
    }, [open, close]);

    const onMenuKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ?? []);
        const i = items.indexOf(document.activeElement as HTMLButtonElement);
        if (e.key === "Escape" || e.key === "Tab") {
            e.preventDefault();
            close(true);
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            items[(i + 1) % items.length]?.focus();
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            items[(i - 1 + items.length) % items.length]?.focus();
        }
    };

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={Boolean(open)}
                aria-pressed={pressed}
                onClick={() => {
                    if (open) return close(false);
                    const r = buttonRef.current?.getBoundingClientRect();
                    if (r) setOpen({ left: r.left, top: r.bottom + 4 });
                }}
                onKeyDown={(e) => {
                    if (e.key === "ArrowDown" && !open) {
                        e.preventDefault();
                        const r = buttonRef.current?.getBoundingClientRect();
                        if (r) setOpen({ left: r.left, top: r.bottom + 4 });
                    }
                }}
                title="Draw a rectangle, ellipse, line or arrow"
                className="pdf-edit-tool flex h-8 shrink-0 items-center gap-1.5 rounded px-2 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
                <Square className="h-4 w-4" aria-hidden />
                Shape
                <ChevronDown className="h-3.5 w-3.5 opacity-70" aria-hidden />
            </button>
            {open ? (
                <div
                    ref={menuRef}
                    role="menu"
                    aria-label="Shapes"
                    onKeyDown={onMenuKey}
                    style={{ left: open.left, top: open.top }}
                    className="fixed z-50 min-w-36 rounded-md border bg-card p-1 text-sm text-card-foreground shadow-lg"
                >
                    {SHAPES.map(({ kind, label, icon: Icon }) => (
                        <button
                            key={kind}
                            type="button"
                            role="menuitem"
                            onClick={() => {
                                close(false);
                                onPick(kind);
                            }}
                            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                        >
                            <Icon className="h-4 w-4" aria-hidden />
                            {label}
                        </button>
                    ))}
                </div>
            ) : null}
        </>
    );
}
